import React, { useState } from "react";
import Modal from "react-modal";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { HeadLine } from "../../../HeadLine";
import { PrimaryButton, SecondaryButton } from "../../../Button";
import { A } from "../../../Anchor";
import { addDefaultPaymentOption } from "../../thunk";
import { useDispatch, useSelector } from "react-redux";
import { paymentMethodsErrors } from "../../selector";
import { isLoadingSelector } from "ccui-framework";

const Container = styled.div`
  background: #ffffff;
  box-shadow: 0 0 50px rgba(31, 32, 33, 0.08);
  border-radius: 50px 0 0 0;
  z-index: 2;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  padding: 2rem;
  justify-content: space-evenly;
  align-items: center;
  flex-direction: column;
`;

const Actions = styled.div`
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Body = styled.div`
  width: 356px;
`;

const Input = styled.div`
  padding: 0.5rem;
  &:last-of-type {
    margin-top: 25px;
  }
`;

const ErrorMessage = styled.span`
  font-size: 10px;
  padding: 1rem 0 0 0;
  color: red;
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 1rem;
  cursor: pointer;
`;

const ComplianceText = styled.p`
  text-align: center;
  font-size: 12px;
  width: 90%;
`;

const CModal = styled(Modal)`
  border-radius: 50px 0 0 0;
`;

const AddAccountModal = ({ isOpen, closeModal }) => {
  const dispatch = useDispatch();
  const errors = useSelector((state) => paymentMethodsErrors(state));
  const isLoading = useSelector((state) => isLoadingSelector(state));
  const [routingNumber, setRoutingNumber] = useState("");
  const [routingNumberError, setRoutingError] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountNumberError, setAccountNumberError] = useState("");
  const [accountType, setAccountType] = useState("");
  const [accountTypeError, setAccountTypeError] = useState("");

  const onRoutingNumberChange = (routingNum) => {
    const ROUTING_NUMBER_REGEX = /^[0-9]+$/;
    const ROUTING_NUMBER_LENGTH = 9;

    if (routingNum) {
      if (!ROUTING_NUMBER_REGEX.test(routingNum)) {
        setRoutingError("Routing number invalid.");
      } else if (routingNum.length !== ROUTING_NUMBER_LENGTH) {
        setRoutingError("Routing number must be 9 digits.");
      } else {
        setRoutingNumber(routingNum);
        setRoutingError("");
      }
    } else {
      setRoutingError("Routing number is required.");
    }
  };

  const onAccountNumberChange = (accountNum) => {
    const ACCOUNT_NUMBER_REGEX = /^[0-9]+$/;
    const ACCOUNT_NUMBER_MIN_LENGTH = 4;
    const ACCOUNT_NUMBER_MAX_LENGTH = 19;

    if (accountNum) {
      if (!ACCOUNT_NUMBER_REGEX.test(accountNum)) {
        setAccountNumberError("Account number invalid.");
      } else if (accountNum.length < ACCOUNT_NUMBER_MIN_LENGTH) {
        setAccountNumberError("Account number must at least 4 digits.");
      } else if (
        accountNum.length >= ACCOUNT_NUMBER_MIN_LENGTH &&
        accountNum.length > ACCOUNT_NUMBER_MAX_LENGTH
      ) {
        setAccountNumberError(
          "Account number must not be more than 17 digits."
        );
      } else {
        setAccountNumber(accountNum);
        setAccountNumberError("");
      }
    } else {
      setAccountNumberError("Account number required.");
    }
  };

  const onAccountTypeChange = (accountType) => {
    if (!["personal", "business"].includes(accountType)) {
      setAccountTypeError("Account type required.");
    } else {
      setAccountTypeError("");
      setAccountType(accountType);
    }
  };
  const submitButtonDisabled =
    routingNumberError ||
    accountNumberError ||
    accountTypeError ||
    (accountType && routingNumber && accountNumber);

  return (
    <CModal
      className="Modal__Bootstrap modal-dialog"
      isOpen={isOpen}
      contentLabel="Add_Account"
      style={{ borderRadius: "50px 0 0 0" }}
    >
      <Container>
        <Wrapper>
          <CloseIcon onClick={closeModal}>
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.7071 0.293C10.5196 0.105529 10.2652 0.000213623 10.0001 0.000213623C9.73492 0.000213623 9.48061 0.105529 9.29308 0.293L6.00008 3.586L2.70708 0.293C2.51848 0.110842 2.26588 0.0100477 2.00368 0.0123261C1.74148 0.0146045 1.49067 0.119773 1.30526 0.305182C1.11985 0.49059 1.01469 0.741402 1.01241 1.0036C1.01013 1.2658 1.11092 1.5184 1.29308 1.707L4.58608 5L1.29308 8.293C1.19757 8.38525 1.12139 8.49559 1.06898 8.6176C1.01657 8.7396 0.988985 8.87082 0.987831 9.0036C0.986677 9.13638 1.01198 9.26806 1.06226 9.39095C1.11254 9.51385 1.18679 9.6255 1.28069 9.71939C1.37458 9.81329 1.48623 9.88754 1.60913 9.93782C1.73202 9.9881 1.8637 10.0134 1.99648 10.0123C2.12926 10.0111 2.26048 9.98351 2.38249 9.9311C2.50449 9.87869 2.61483 9.80251 2.70708 9.707L6.00008 6.414L9.29308 9.707C9.48168 9.88916 9.73429 9.98995 9.99648 9.98767C10.2587 9.9854 10.5095 9.88023 10.6949 9.69482C10.8803 9.50941 10.9855 9.2586 10.9878 8.9964C10.99 8.7342 10.8892 8.4816 10.7071 8.293L7.41408 5L10.7071 1.707C10.8946 1.51947 10.9999 1.26516 10.9999 1C10.9999 0.734836 10.8946 0.480528 10.7071 0.293Z"
                fill="#C4C4C4"
              />
            </svg>
          </CloseIcon>
          <HeadLine headingText={"Add Account"} />
          <ComplianceText>
            Bank accounts must be verified before use. After adding the account,
            please email{" "}
            <A href={`mailto:compliance@cardcash.com`}>
              compliance@cardcash.com
            </A>{" "}
            to have the account verified.
          </ComplianceText>
          <Body>
            <Input>
              <label htmlFor="routingNumber">Routing Number</label>
              <input
                type="text"
                name="routingNumber"
                placeholder="Routing Number"
                onChange={(e) => onRoutingNumberChange(e.target.value.trim())}
              />
              {routingNumberError && (
                <ErrorMessage>{routingNumberError}</ErrorMessage>
              )}
            </Input>
            <Input>
              <label htmlFor="accountNumber">Account Number</label>
              <input
                type="text"
                name="accountNumber"
                onChange={(e) => onAccountNumberChange(e.target.value.trim())}
                placeholder="Account Number"
              />
              {accountNumberError && (
                <ErrorMessage>{accountNumberError}</ErrorMessage>
              )}
            </Input>
            <Input>
              <label htmlFor="accountType">Account Type</label>
              <select
                name="dropdown"
                className="form-control"
                onChange={(e) => onAccountTypeChange(e.target.value.trim())}
              >
                <option>Choose</option>
                <option value="personal">Personal</option>
                <option value="business">Business</option>
              </select>

              {accountTypeError && (
                <ErrorMessage>{accountTypeError}</ErrorMessage>
              )}
            </Input>
            <Actions>
              <PrimaryButton
                onClick={() => {
                  dispatch(
                    addDefaultPaymentOption(
                      {
                        paymentMethod: "ACH",
                        bankAccount: {
                          routingNumber: routingNumber,
                          accountNumber: accountNumber,
                          accountType: accountType,
                          save: true,
                        },
                        default: false,
                        type: "sell",
                      },
                      closeModal
                    )
                  );
                }}
                disabled={!submitButtonDisabled}
              >
                {!isLoading ? "Add" : "Loading..."}
              </PrimaryButton>
              {errors && <ErrorMessage>Error: {errors}</ErrorMessage>}
              <SecondaryButton onClick={closeModal}>Cancel</SecondaryButton>
            </Actions>
          </Body>
        </Wrapper>
      </Container>
    </CModal>
  );
};

AddAccountModal.prototype = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export { AddAccountModal };
